import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from 'config';

interface Partner {
  _id: string;
  name: string;
  imageUrl: string;
  order: number;
}

interface PartnerCategory {
  _id: string;
  name: string;
  partners: Partner[];
  order: number;
}

const PartnersPage = () => {
  const [categories, setCategories] = useState<PartnerCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Create skeleton data for loading state
  const skeletonCategories = Array(4).fill(null).map((_, index) => ({
    id: index,
    companies: Array(6).fill(null)
  }));

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API_URL}/api/partners`);
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching partners:', error);
        // Don't clear categories on error to maintain skeleton layout
      } finally {
        setIsLoading(false);
      }
    };

    fetchPartners();
  }, []);

  // Render skeleton loading state
  const renderSkeleton = () => (
    <div className="space-y-12">
      {skeletonCategories.map((group) => (
        <div key={group.id}>
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6 animate-pulse"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {group.companies.map((_, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg shadow-md p-6"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
                  <div className="h-6 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  // Render actual content
  const renderContent = () => (
    <div className="space-y-12">
      {categories.map((category) => (
        <div key={category._id}>
          <h2 className="text-2xl font-semibold mb-6">{category.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {category.partners.map((partner) => (
              <div 
                key={partner._id} 
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-gray-50">
                    <img 
                      src={`${API_URL}${partner.imageUrl}`}
                      alt={partner.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h3 className="text-lg font-semibold">{partner.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Наши партнеры</h1>
      
      <p className="text-lg text-gray-600 mb-8">
        Мы гордимся сотрудничеством с ведущими компаниями России в различных отраслях. 
        Наши партнеры доверяют нам свои проекты, а мы обеспечиваем их качественным оборудованием и профессиональной поддержкой.
      </p>

      {/* Render either skeleton or content */}
      {isLoading || categories.length === 0 ? renderSkeleton() : renderContent()}

      <div className="mt-12 bg-gray-50 rounded-lg p-8">
        <h2 className="text-2xl font-semibold mb-4">Станьте нашим партнером</h2>
        <p className="text-gray-600 mb-6">
          Мы открыты к новому сотрудничеству и готовы предложить выгодные условия для вашего бизнеса.
        </p>
        <Link 
          to="/callback"
          className="bg-primary text-white px-8 py-3 rounded-md hover:bg-primary-dark transition-colors"
        >
          Стать партнером
        </Link>
      </div>
    </div>
  );
};

export default PartnersPage; 